import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withHashLocation } from '@angular/router';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';

import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { API_BASE_URL as USERS_API_BASE_URL } from './features/admin/users/api/generated/users.client';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';
import { provideCordFormly } from 'ds';
import { ConfirmationService, MessageService } from 'primeng/api';
import { getBffBaseUrl } from './core/bff-base-url.provider';
import { API_BASE_URL as ACCESS_API_BASE_URL } from './features/access/api/generated/access.client';
import { API_BASE_URL as FEATURE_PERMISSIONS_API_BASE_URL } from './features/admin/feature-permissions/api/generated/feature-permissions.client';
import { API_BASE_URL as FEATURE_API_BASE_URL } from './features/admin/feature/api/generated/feature.client';
import { API_BASE_URL as PERMISSIONS_API_BASE_URL } from './features/admin/permissions/api/generated/permissions.client';
import { API_BASE_URL as ROLES_API_BASE_URL } from './features/admin/roles/api/generated/roles.client';
import { API_BASE_URL as AUTH_API_BASE_URL } from './features/auth/api/generated/auth.client';
import { authRefreshInterceptor } from './features/auth/interceptors/auth-refresh.interceptor';
import { authInterceptor } from './features/auth/interceptors/auth.interceptor';
import { requestIdInterceptor } from './features/auth/interceptors/request-id.interceptor';
import { API_BASE_URL as BRANDS_API_BASE_URL } from './features/catalog/brands/api/generated/brands.client';
import { API_BASE_URL as CATEGORIES_API_BASE_URL } from './features/catalog/categories/api/generated/categories.client';
import { API_BASE_URL as COLLECTIONS_API_BASE_URL } from './features/catalog/collections/api/generated/collections.client';
import { API_BASE_URL as PRODUCTS_API_BASE_URL } from './features/catalog/products/api/generated/products.client';
import { API_BASE_URL as SKUS_API_BASE_URL } from './features/catalog/skus/api/generated/skus.client';
import { API_BASE_URL as REDIRECTIONS_API_BASE_URL } from './features/cms/redirections/api/generated/redirections.client';
import { API_BASE_URL as SELLERS_API_BASE_URL } from './features/marketplace/sellers/api/generated/sellers.client';
import { API_BASE_URL as SUGESTIONS_API_BASE_URL, API_BASE_URL as SUGGESTIONS_API_BASE_URL } from './features/marketplace/suggestions/api/generated/suggestions.client';
import { API_BASE_URL as MERCHANDISING_RULES_API_BASE_URL } from './features/search/merchandising-rules/api/generated/merchandising-rules.client';
import { API_BASE_URL as RELEVANCE_RULES_API_BASE_URL } from './features/search/relevance-rules/api/generated/relevance-rules.client';
import { API_BASE_URL as USER_API_BASE_URL } from './features/settings/api/generated/user-profile.client';
import { API_BASE_URL as EXPORTS_API_BASE_URL } from './shared/services/exports/api/generated/exports.client';
import { API_BASE_URL as IMPORTS_API_BASE_URL } from './shared/services/imports/api/generated/imports.client';

import { API_BASE_URL as ATTRIBUTE_API_BASE_URL } from './features/catalog/attributes/api/generated/attributes.client';
import { API_BASE_URL as SKU_IMAGES_API_BASE_URL } from './features/catalog/sku-images/api/generated/sku-images.client';
import { API_BASE_URL as SKU_SERVICES_API_BASE_URL } from './features/catalog/sku-services/api/generated/sku-services.client';
import { API_BASE_URL as PRICING_API_BASE_URL } from './features/catalog/pricing/api/generated/pricing.client';
import { API_BASE_URL as PRICE_QUARANTINE_API_BASE_URL } from './features/catalog/price-quarantine/api/generated/price-quarantine.client';
import { API_BASE_URL as PRICING_GUARDRAILS_API_BASE_URL } from './features/catalog/pricing-guardrails/api/generated/pricing-guardrails.client';
import { API_BASE_URL as STOCK_API_BASE_URL } from './features/catalog/stock/api/generated/stock.client';
import { API_BASE_URL as REDIRECTS_RULES_API_BASE_URL } from './features/search/redirects-rules/api/generated/redirects-rules.client';
import { API_BASE_URL as BANNED_TERMS_API_BASE_URL } from './features/search/banned-terms/api/generated/banned-terms.client';
import { API_BASE_URL as SIMULATE_API_BASE_URL } from './features/search/simulate/api/generated/simulate.client';
import { API_BASE_URL as SALES_CONTEXT_API_BASE_URL } from './features/catalog/sales-context/api/generated/sales-context.client';

import { API_BASE_URL as WAREHOUSES_COVERAGE_API_BASE_URL } from './features/fulfillment/warehouses/api/generated/warehouses-coverage.client';
import { API_BASE_URL as WAREHOUSES_API_BASE_URL } from './features/fulfillment/warehouses/api/generated/warehouses.client';

import { API_BASE_URL as AUDITING_API_BASE_URL } from './features/auditing/events/api/generated/auditing-events.client';
import { API_BASE_URL as SYNONYMS_RULES_API_BASE_URL } from './features/search/synonyms-rules/api/generated/synonyms-rules.client';
import { API_BASE_URL as STATUS_API_BASE_URL } from './features/indexer/status/api/generated/status.client';
import { API_BASE_URL as RECONCILIATION_API_BASE_URL } from './features/indexer/reconciliation/api/generated/reconciliation.client';
import { API_BASE_URL as MANAGEMENT_API_BASE_URL } from './features/orders/management/api/generated/management.client';
import { API_BASE_URL as USER_ROLES_API_BASE_URL } from './features/admin/users/api/generated/user-roles.client';
import { API_BASE_URL as VTEX_SYNC_API_BASE_URL } from './features/catalog/vtex-sync/api/generated/vtex-sync.client';
import { API_BASE_URL as PROMOTIONS_API_BASE_URL } from './features/inventory/promotions/api/generated/promotions.client';

import { API_BASE_URL as CUSTOMERS_API_BASE_URL } from './features/customers/customers/api/generated/customers.client';
import {API_BASE_URL as SEGMENTS_API_BASE_URL} from './features/customers/segments/api/generated/segments.client';  
import { API_BASE_URL as USAGE_API_BASE_URL } from './features/dashboard/api/generated/usage.client';
import { API_BASE_URL as UBIGEOS_API_BASE_URL } from './features/inventory/promotions/api/generated/ubigeos.client';
import { API_BASE_URL as STORES_API_BASE_URL } from './features/admin/stores/api/generated/stores.client';
import { API_BASE_URL as MIGRATIONS_API_BASE_URL } from './features/migrations/api/generated/migrations.client';
import { StoreContextsStore } from './features/auth/state/store-contexts.store';
import { PRIMENG_TRANSLATION_ES } from './shared/config/primeng-translation-es';

export const appConfig: ApplicationConfig = {
  providers: [
    ConfirmationService,
    {
      provide: APP_INITIALIZER,
      useFactory: (store: StoreContextsStore) => {
        return () => {
          store.loadContexts();
        };
      },
      deps: [StoreContextsStore],
      multi: true,
    },
    provideHttpClient(withInterceptors([requestIdInterceptor, authInterceptor, authRefreshInterceptor])),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withHashLocation()
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.app-dark'  //fix para que no tome estilos desde el OS
        }
      },
      // Calendarios en español y con formato dd/mm/aaaa. PrimeNG toma días, meses y
      // formato de fecha solo de esta configuración (no hay idioma por componente).
      translation: PRIMENG_TRANSLATION_ES,
    }),
    {
      provide: AUTH_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: ACCESS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: PERMISSIONS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: FEATURE_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: FEATURE_PERMISSIONS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {      
      provide: ROLES_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {      
      provide: USER_ROLES_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: BRANDS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: IMPORTS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: EXPORTS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: CATEGORIES_API_BASE_URL,
      useFactory: getBffBaseUrl
    },

    {
      provide: SUGGESTIONS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: MERCHANDISING_RULES_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },

    {
      provide: PRODUCTS_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: SKUS_API_BASE_URL,
      useFactory: getBffBaseUrl
    },

    {
      provide: ATTRIBUTE_API_BASE_URL,
      useFactory: getBffBaseUrl
    },

    {
      provide: SUGESTIONS_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: COLLECTIONS_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: REDIRECTIONS_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: SKU_SERVICES_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: PRICING_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: PRICE_QUARANTINE_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: PRICING_GUARDRAILS_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: REDIRECTS_RULES_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: STOCK_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: SYNONYMS_RULES_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: BANNED_TERMS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: SIMULATE_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    importProvidersFrom(
      FormlyPrimeNGModule
    ),
    {
      provide: SELLERS_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    {
      provide: ROLES_API_BASE_URL,
      useFactory: getBffBaseUrl
    },
    provideCordFormly(),
    MessageService,
    { provide: USER_API_BASE_URL, useFactory: getBffBaseUrl },

    {
      provide: RELEVANCE_RULES_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: SKU_IMAGES_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: WAREHOUSES_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: WAREHOUSES_COVERAGE_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: AUDITING_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: STATUS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: RECONCILIATION_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide:MANAGEMENT_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: USERS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: SALES_CONTEXT_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: VTEX_SYNC_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
      provide: PROMOTIONS_API_BASE_URL,
      useFactory: getBffBaseUrl,
    },
    {
        provide: CUSTOMERS_API_BASE_URL,
        useFactory: getBffBaseUrl,
      },
      {
        provide: SEGMENTS_API_BASE_URL,
        useFactory: getBffBaseUrl,
      },
      {
        provide: USAGE_API_BASE_URL,
        useFactory: getBffBaseUrl,
      },
      {
        provide: UBIGEOS_API_BASE_URL,
        useFactory: getBffBaseUrl,
      },
      {
        provide: STORES_API_BASE_URL,
        useFactory: getBffBaseUrl,
      },
      {
        provide: MIGRATIONS_API_BASE_URL,
        useFactory: getBffBaseUrl,
      }
  ],
};

